<template>
    <ui-container class="space-y-8 py-8" v-if="nonConformanceReport">

        <!-- Header. -->
        <ui-header :title="`Non-Conformance Report #${nonConformanceReport.reference}`"
                   :subtitle="nonConformanceReport.status">
            <template #actions>
                <ui-button-new icon="fas fa-pencil" @click="edit()">Edit</ui-button-new>
                <ui-button-new icon="fas fa-trash" danger @click="openDeleteModal">Delete</ui-button-new>
            </template>
        </ui-header>

        <ui-tabs active="overview" :tabs="tabs" #default="{ isActive }">
            <!-- Overview. -->
            <div v-if="isActive('overview')">
                <ui-data-grid>
                    <ui-data-grid-item label="Package" wide :value="nonConformanceReport.packageName">
                    </ui-data-grid-item>
                    <ui-data-grid-item label="Title" wide :value="nonConformanceReport.title">
                    </ui-data-grid-item>
                    <ui-data-grid-item wide>
                        <ui-input-file-inline label="Image and Videos"
                                              name="media[]"
                                              :filesystemId="nonConformanceReport.inlineFilesystemId"
                                              :readonly="true"
                                              :downloadUrl="downloadUrl"
                                              :current-files="getNonConformanceReportFiles">
                        </ui-input-file-inline>
                    </ui-data-grid-item>
                    <ui-data-grid-item label="Date">
                        <template><span :class="nonConformanceReport.date?'':'text-gray-400'">{{
                                nonConformanceReport.date ? nonConformanceReport.formattedDate : 'No date supplied'
                            }}</span></template>
                    </ui-data-grid-item>
                    <ui-data-grid-item label="Contract Reference" :value="nonConformanceReport.contractReference"
                    ></ui-data-grid-item>
                    <ui-data-grid-item label="Location of Works" :value="nonConformanceReport.locationOfWorks"
                    ></ui-data-grid-item>
                    <ui-data-grid-item label="Site Address" :value="nonConformanceReport.siteAddress"
                                       wide></ui-data-grid-item>
                    <ui-data-grid-item label="What is the main reason or what when wrong?"
                                       :value="nonConformanceReport.whatWentWrong"
                                       wide></ui-data-grid-item>
                    <ui-data-grid-item label="Why doesn't the work meet specs?"
                                       :value="nonConformanceReport.whyNotMeetingSpec"
                                       wide></ui-data-grid-item>
                    <ui-data-grid-item label="What can be done to prevent it from happening again?"
                                       :value="nonConformanceReport.whatToPrevent"
                                       wide></ui-data-grid-item>
                    <ui-data-grid-item label="Explanatory corrective action taken"
                                       :value="nonConformanceReport.correctiveActionTaken"
                                       wide></ui-data-grid-item>
                    <ui-data-grid-item label="Key players involved in the NCR and specs affected under the NCR"
                                       :value="nonConformanceReport.keyPlayers"
                                       wide></ui-data-grid-item>
                    <ui-data-grid-item v-if="nonConformanceReport.createdBy" label="Created By"
                                       :value="nonConformanceReport.createdBy"></ui-data-grid-item>
                    <ui-data-grid-item wide :key="sentLogKey">
                        <send-log :send-log="combineSendLogs" @revoke="revoke" @resend="resend"></send-log>
                    </ui-data-grid-item>
                    <ui-data-grid-item label="Quick action" wide>
                        <div class="flex flex-col md:flex-row flex-wrap -m-2">
                            <ui-button-new v-if="nonConformanceReport.status=='Open'" class="m-2"
                                           icon="fas fa-check-circle"
                                           @click="closeNonConformanceReport">Close
                            </ui-button-new>
                            <ui-button-new v-else-if="nonConformanceReport.status=='Closed'" class="m-2"
                                           icon="fas fa-dot-circle"
                                           @click="reopenNonConformanceReport">Re-Open
                            </ui-button-new>
                            <ui-button-new v-else-if="nonConformanceReport.status=='Draft'" class="m-2" primary
                                           icon="fas fa-dot-circle"
                                           @click="openNonConformanceReport">Open
                            </ui-button-new>
                            <ui-button-new class="m-2" icon="fas fa-route" extra @click="send">
                                {{ 'Send' }}
                            </ui-button-new>
                        </div>
                    </ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Files. -->
            <div v-else-if="isActive('files')" style="height:calc(100vh - 400px);">
                <fs
                    :filesystem-id="nonConformanceReport.filesystemId"
                ></fs>
            </div>

            <!-- Notes. -->
            <div v-else-if="isActive('notes')" style="height:calc(100vh - 400px);">
                <notes-v2 :get-url="nonConformanceReport.getNotesUrl()"
                          :post-url="nonConformanceReport.postNotesUrl()"></notes-v2>
            </div>

        </ui-tabs>
    </ui-container>
</template>

<script>
import {NonConformanceReport} from "@models";
import NotesV2 from "../../NotesV2.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiInputFileInline from "../../filesystem/inline/UiInputFileInline.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import SendLog from "../../send-log/SendLog.vue";
import moment from "moment/moment";

export default {
    components: {SendLog, UiDataGridItem, UiButtonNew, UiInputFileInline, UiDropdownItem, NotesV2},
    props: {
        item: Object,
        edit: Function,
        removeItem: Function,
    },
    data() {
        return {
            nonConformanceReport: null,
            sentLogKey:0,
            tabs: [
                {value: 'overview', label: 'Overview'},
                {value: 'files', label: 'Files'},
                {value: 'notes', label: 'Notes'},
            ],
        }
    },
    computed: {
        combineSendLogs() {
            return this.nonConformanceReport.sendLog.map((a) => {
                return a;
            }).sort((a, b) => moment(b.requested.at).unix() - moment(a.requested.at).unix())
        }
    },
    methods: {
        revoke(i) {
            this.nonConformanceReport.revoke(i.id);
        },
        resend(i) {
            this.nonConformanceReport.resend(i);
        },
        send() {
            this.$modals.open('non-conformance-report-send-modal', {
                title: `Send Non-Conformance Report`,
                button: 'Send Non-Conformance Report',
                nonConformanceReport: this.item,
                handle: async (messageId, modal) => {
                    modal.close()
                }
            })
        },

        isMimeTypeSupported(mimeType) {
            return ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/heic'].includes(mimeType.toLowerCase())
        },
        async closeNonConformanceReport() {
            await this.nonConformanceReport.close()
            this.item.status = this.nonConformanceReport.status

        },
        async reopenNonConformanceReport() {
            await this.nonConformanceReport.reopen()
            this.item.status = this.nonConformanceReport.status
        },
        async openNonConformanceReport() {
            await this.nonConformanceReport.reopen()
            this.item.status = this.nonConformanceReport.status
        },
        downloadUrl(id) {
            return route('api.v2.hsqe.non-conformance-reports.file.download', {
                'id': this.item.id,
                'package_reference': this.item.packageReference,
                'file_id': id
            })
        },
        getNonConformanceReportFiles() {
            return this.nonConformanceReport.files
        },
        toggleEditMode(mode) {
            this.edit();
        },
        openDeleteModal() {
            const title = 'Are you sure you want to delete Non-Conformance Report #' + this.item.reference + '?'
            const description = 'Deleting this Non-Conformance Report is irreversible. Are you sure you want to delete it?'
            eventBus.$emit('delete-modal-open', title, description, async () => {
                await this.item.delete()
                this.removeItem(this.item)

            });
        },
        async updateFromListener(data) {
            if (data.operation === 'updateAggregate') {
                if ((data.aggregate.type === 'NonConformanceReport') && (data.aggregate.id === this.nonConformanceReport.id)) {
                    Object.assign(this.nonConformanceReport, data.aggregate.data)
                    Object.assign(this.item, data.aggregate.data)
                    this.sentLogKey ++;
                }
            }
        },

    },
    async mounted() {
        this.nonConformanceReport = await NonConformanceReport.find(this.item.packageReference, this.item.id)
        if (this.nonConformanceReport) {
            User.registerAggregate({
                'type': 'NonConformanceReport',
                'id': this.nonConformanceReport.id
            }, this.updateFromListener)
        }

    }
}
</script>
